import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const CVSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const documents = [
    {
      title: 'CV',
      filename: '01EminHasanov.pdf',
      downloadLabel: t('cv.download_cv'),
    },
    {
      title: 'Resume',
      filename: '01EminHasanovResume.pdf',
      downloadLabel: t('cv.download_resume'),
    },
  ];

  const handleDownload = (filename) => {
    const link = document.createElement('a');
    link.href = `/cv/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section
      id="cv"
      data-testid="cv-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0F172A]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('cv.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('cv.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          {documents.map((doc, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <Card
                data-testid="content-card"
                className="bg-[#1A1F3A] rounded-2xl p-6 md:p-8 border border-[#22D3EE]/20 hover:border-[#22D3EE]/50 hover:shadow-[0_0_30px_rgba(34,211,238,0.15)] transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="p-4 rounded-full bg-[#22D3EE]/10 border-2 border-[#22D3EE]/30">
                    <FileText className="w-12 h-12 text-[#22D3EE]" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-[#F8FAFC] mb-2">
                      {doc.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">
                      {t('cv.last_updated')}: 2024
                    </p>
                  </div>

                  <Button
                    onClick={() => handleDownload(doc.filename)}
                    data-testid="cv-download-button"
                    className="w-full px-6 py-3 rounded-full font-semibold text-base bg-gradient-to-r from-[#22D3EE] to-[#14B8A6] text-[#0A0E27] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:scale-105 transition-all duration-300"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {doc.downloadLabel}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
